import React, { useEffect, useRef, useState } from 'react';
import Select from "react-select";
import { ClipboardPlus, Copy, Eye, Loader, Mic } from 'lucide-react';
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook.jsx";
import { can_visit, getPractice, notify } from '../../helpers/index.js';
import { Config } from '../../Config.js';
import Spacing from '../Spacing.jsx';
import { fetchDocflowTemplatePrompts, fetchPracticeDocflowTemplates } from '../../services/aiCharting.js';
import BackBtn from '../Button/BackBtn.jsx';
import ViewDocflowDetailsModal from '../Modal/ViewDocflowDetailsModal.jsx';
import DocflowRecordingModal from '../Modal/DocflowRecordingModal.jsx';
import { userAccessToken } from "../../helpers";


export default function CreateDocFlow({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const messageBoxRef = useRef(null);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [docflowTemplateDetails, setDocflowTemplateDetails] = useState("");
  const [allDocflowTemplate, setAllDocflowTemplate] = useState([]);
  const [docflowTemplate, setDocflowTemplate] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [viewTemplateModal, setViewTemplateModal] = useState(false);
  const [openRecordingModal, setOpenRecordingModal] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);


  const loadDocflowAllDocflowTemplate = async (accountId) => {
    try {
      const response = await fetchPracticeDocflowTemplates(accountId);
      if(response?.data) {
        const allDocflowTemplate = response?.data?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllDocflowTemplate(allDocflowTemplate);
      }
    } catch (error) {
      // console.log("🚀 ~ loadDocflowDocflowTemplate ~ error:", error);
    }
  };

  useEffect(() => {
    loadDocflowAllDocflowTemplate(account_id);
  }, [account_id]);

  // Handle message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const userInput = input.trim();
    if (!userInput) return;
    setButtonLoader(true);

    // Add user message to chat
    setMessages((prevMessages) => [
      ...prevMessages,
      { type: "user", content: userInput },
      { type: "ai", content: "Thinking..." },
    ]);

    try {
      const response = await fetch(`${Config.apiServerUrl}/ai/docflow`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          Authorization: `Bearer ${userAccessToken()}`,
        },
        body: JSON.stringify({
          account_id: account_id,
          location_id: location_id,
          docflow_template_id: docflowTemplate,
          user_input: userInput,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;
      let finalResponse = "";

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          finalResponse += chunk;

          // Update only the last AI message
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1] = {
              type: "ai",
              content: finalResponse,
            };
            return updatedMessages;
          });
        }
      }
      
      document.querySelectorAll('.docpro_message_item_text:last-of-type').forEach(function(element) {
        element.innerHTML = element.innerHTML.replace("```html", "").replace("```", "").replace("&lt;/<", "<");        
      });

    } catch (error) {
      console.error("Error fetching response:", error);
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1),
        {
          type: "ai",
          content: "An error occurred while fetching the response.",
        },
      ]);
    } finally {
      setButtonLoader(false);
    }
  };

  useEffect(() => {
    if (messageBoxRef.current) {
      messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const handleCopy = (id) => {
    const content = document.querySelector('.GPT_response_'+id);
    const range = document.createRange();
    range.selectNode(content);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range); 
    document.execCommand('copy');
    window.getSelection().removeAllRanges();

    notify("success", "Content copied to clipboard!", 0.5);
  };

  const handleDocflowTemplateChange = (selectedOption) => {
    setDocflowTemplate(selectedOption?.value || null);
    setDocflowTemplateDetails("");
  };

  const fetchTemplateDetails = () => {
    setViewTemplateModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
    getDocflowDetails(account_id, docflowTemplate);
  };

  const getDocflowDetails = async (accountId, id) => {
    try {
      const response = await fetchDocflowTemplatePrompts(accountId, id);
      if(response){
        if(!response?.detail && response?.data?.status === 'success') {
          setDocflowTemplateDetails(response?.data?.result?.prompt_instructions);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getDocflowDetails ~ error:", error);
    }
  };

  useDocumentTitle("DocFlow - Smart Chart");
  
  return (
    <>
      <BackBtn href="/ai-charting" btnText="Back to AI charting" />
      <Spacing lg="0" sm="24" />
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_iris_bg docpro_white_color">
            <ClipboardPlus width={20} height={20} />
          </i>
          DocFlow - Smart Chart
        </h2>
      </div>

      <div className="docpro_grid docpro_grid_gap_20 docpro_grid_col_1_3">
        <div className='docpro_data_table_wrap'>
          <div className='docpro_data_table_headings'>
            <h3 className='docpro_mb_0 docpro_fs_18 docpro_fw_600 mx-1'>Instructions</h3>
          </div>
          <hr />
          <div className='p-3 mx-1 mb-2'>
            <form onSubmit={handleSubmit}>
              <div className='flex GPT_instructions'>
                <div>
                  <label className="docpro_fw_500 docpro_primary_color flex items-center" style={{ justifyContent: 'space-between' }}>
                    <span>Select Template <span className="docpro_red_color">*</span></span>
                    {(docflowTemplate && can_visit("administrator_docflow_template")) && (
                      <button type='button' 
                        className="docpro_icon_btn overview" style={{ width: 'auto', height: 'auto', borderRadius: '50%' }}
                        onClick={fetchTemplateDetails}
                      >
                        <Eye width={16} height={16} color='blue' />
                      </button>
                    )}
                  </label>
                  <Select
                    required
                    options={allDocflowTemplate}
                    placeholder="Search..."
                    isSearchable
                    value={allDocflowTemplate?.find((option) => option?.value === docflowTemplate) || null}
                    onChange={handleDocflowTemplateChange}
                    className="react-select-container w-full"
                    classNamePrefix="react-select"
                    filterOption={(option, rawInput) => {
                      const words = rawInput.toLowerCase().split(" ");
                      return words.every((word) =>
                        option.label.toLowerCase().includes(word)
                      );
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '40px',
                        borderColor: 'var(--border)',
                        borderRadius: '8px'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 1050,
                        marginTop: '-2px',
                        borderRadius: '0 0 3px 3px',
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1050,
                        padding: 0
                      }),
                    }}
                  />
                  <Spacing lg="20" sm="20" />

                  <label className="docpro_fw_500 docpro_primary_color flex items-center" style={{ justifyContent: 'space-between' }}>
                    <span>Write Additional Details <span className="docpro_red_color">*</span></span>
                    <button type='button' 
                      className="docpro_icon_btn overview record" style={{ width: 'auto', height: 'auto', borderRadius: '50%' }}
                      onClick={() => {
                        setOpenRecordingModal(true);
                        setPreviousScrollPosition(window.scrollY);
                        window.scrollTo(0, 0);
                      }}
                    >
                      <Mic width={20} height={20} />
                      <span>Record</span>
                      <span className='docpro_tip'>Voice Transcription</span>
                    </button>
                  </label>
                  <textarea
                    required
                    minLength={2}
                    autoFocus
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="docpro_form_field resize-none"
                    cols="30"
                    rows="18"
                    placeholder="Enter user message..."
                  ></textarea>
                  <Spacing lg="20" sm="20" />
                </div>

                <div className="docpro_modal_btns" style={{ marginTop: 'auto', justifyContent: 'space-between' }}>
                  <button
                    type='reset'
                    className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    style={{ backgroundColor: '#ff2b50', borderColor: '#ff2b50' }}
                    onClick={() => {setInput(""); setDocflowTemplate(null);}}
                  >
                    <span>Clear</span>
                  </button>
                  <button
                    type='submit'
                    className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    disabled={buttonLoader ? true : false}
                  >
                    <span>Generate</span>
                    {buttonLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className='docpro_data_table_wrap'>
          <div className='docpro_data_table_headings'>
            <h3 className='docpro_mb_0 docpro_fs_18 docpro_fw_600 mx-1'>Assistant Result</h3>
          </div>
          <hr />
          <div className='p-3 mx-1 mb-2'>
            <div className='message-box' style={{ marginBottom: '0' }} ref={messageBoxRef}>
              {messages.map((message, index) => (
                <div key={index} className='docflow_chat'>            
                  {message.type === "user" ?
                    <div className="right-msg-data" style={{ backgroundColor: 'e9e9e954' }}>
                      <div className={`flex-grow-3 GPT_response_${index}`} style={{ whiteSpace: 'pre-wrap', color: 'var(--primary)' }}>
                        {message.content}
                      </div>
                    </div>
                  :
                    <div className="left-msg-data" >
                      <div className="shoutbox-flex">
                        <div className="docpro_message_item_avatar shoutbox_img_avatar">
                          AI{" "}
                        </div>
                        <div className='relative'>
                          <div className='docpro_message_item_text' style={{ backgroundColor: '#f6f6f6' }}>
                            <span className={`GPT_response_${index}`} dangerouslySetInnerHTML={{ __html: message.content }} />
                          </div>
                          {message.content && (
                            <button className='absolute top-0 right-0 docpro_bordered_btn docpro_fw_600 docpro_filed_btn copy' onClick={() => handleCopy(index)}><Copy height={16} /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {viewTemplateModal && (
        <ViewDocflowDetailsModal 
          details = {docflowTemplateDetails}
          activeModal = {viewTemplateModal}
          setActiveModal = {setViewTemplateModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {openRecordingModal && (
        <DocflowRecordingModal 
          activeModal = {openRecordingModal}
          setActiveModal = {setOpenRecordingModal}
          setTranscribedText = {setInput}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
